import { useLocalization } from "@fluent/react"
import {
    Alert,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material"
import DownloadIcon from "@mui/icons-material/Download"
import { unwrapResult } from "@reduxjs/toolkit"
import { useEffect, useState } from "react"
import { RestoreIconButton } from "../../../app/components/iconButtons/RestoreIconButton"
import { useAppDispatch } from "../../../app/hooks"
import { showError, showSuccess } from "../../notifications/notificationsSlice"
import {
    downloadReportTemplate,
    loadReportTemplateVersions,
    problemMessage,
    ReportTemplateDto,
    ReportTemplateVersionDto,
    restoreReportTemplateVersion,
    saveBlob,
    TemplateValidationReport,
} from "./reportTemplatesApi"
import { TemplateReportView } from "./TemplateReportView"

interface ReportTemplateVersionsDialogProps {
    template: ReportTemplateDto
    onClose: () => void
    /** Called after a restore added a new current version. */
    onChanged: (template: ReportTemplateDto) => void
}

const formatSize = (bytes: number) =>
    bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`

export const ReportTemplateVersionsDialog = ({ template, onClose, onChanged }: ReportTemplateVersionsDialogProps) => {
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()

    const [versions, setVersions] = useState<ReportTemplateVersionDto[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [busyVersion, setBusyVersion] = useState<number | null>(null)
    const [rejectedReport, setRejectedReport] = useState<TemplateValidationReport | null>(null)

    const genericError = l10n.getString("report-template-action-error", null, "Operacja nie powiodła się.")

    useEffect(() => {
        let active = true
        setIsLoading(true)
        setError(null)

        dispatch(loadReportTemplateVersions(template.id))
            .then(unwrapResult)
            .then(data => {
                if (active) {
                    setVersions([...data].sort((a, b) => b.version - a.version))
                }
            })
            .catch(() => {
                if (active) {
                    setError(genericError)
                }
            })
            .finally(() => {
                if (active) {
                    setIsLoading(false)
                }
            })

        return () => {
            active = false
        }
        // Reload whenever the current version changes (after a restore).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, template.id, template.currentVersion])

    const handleDownload = async (version: number) => {
        setBusyVersion(version)
        try {
            const result = await dispatch(downloadReportTemplate({ id: template.id, version }))
            if (downloadReportTemplate.fulfilled.match(result)) {
                saveBlob(result.payload, `${template.name}_v${version}.docx`)
            } else {
                dispatch(showError("error"))
            }
        } finally {
            setBusyVersion(null)
        }
    }

    const handleRestore = async (version: number) => {
        setBusyVersion(version)
        setRejectedReport(null)
        setError(null)
        try {
            const result = await dispatch(restoreReportTemplateVersion({ id: template.id, version }))
            if (restoreReportTemplateVersion.fulfilled.match(result)) {
                dispatch(showSuccess("report-template-restored"))
                onChanged(result.payload.template)
            } else if (result.payload?.kind === "invalid-template") {
                // The old content no longer passes the current checks (e.g. a tag was retired).
                setRejectedReport(result.payload.report)
            } else {
                setError(problemMessage(result.payload) ?? genericError)
            }
        } finally {
            setBusyVersion(null)
        }
    }

    return (
        <Dialog open maxWidth="md" fullWidth onClose={onClose}>
            <DialogTitle>
                {l10n.getString("report-template-history-title", { name: template.name }, `Historia wersji: ${template.name}`)}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    {error && <Alert severity="error">{error}</Alert>}

                    {rejectedReport && (
                        <>
                            <Alert severity="error">
                                {l10n.getString(
                                    "report-template-restore-rejected",
                                    null,
                                    "Tej wersji nie można przywrócić – nie przechodzi aktualnej walidacji.",
                                )}
                            </Alert>
                            <TemplateReportView report={rejectedReport} showSuccess={false} />
                        </>
                    )}

                    {isLoading ? (
                        <Stack spacing={1}>
                            <Skeleton variant="rounded" height={36} />
                            <Skeleton variant="rounded" height={36} />
                        </Stack>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{l10n.getString("report-template-version", null, "Wersja")}</TableCell>
                                    <TableCell>{l10n.getString("report-template-uploaded-at", null, "Przesłano")}</TableCell>
                                    <TableCell>{l10n.getString("report-template-uploaded-by", null, "Przesłał(a)")}</TableCell>
                                    <TableCell>{l10n.getString("report-template-size", null, "Size")}</TableCell>
                                    <TableCell>{l10n.getString("report-template-remarks", null, "Uwagi")}</TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {versions.map(v => (
                                    <TableRow key={v.version}>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <span>{`v${v.version}`}</span>
                                                {v.isCurrent && (
                                                    <Chip
                                                        size="small"
                                                        color="success"
                                                        label={l10n.getString("report-template-current", null, "Aktualna")}
                                                    />
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>{new Date(v.uploadedAt).toLocaleString()}</TableCell>
                                        <TableCell>{v.uploadedBy}</TableCell>
                                        <TableCell>{formatSize(v.sizeBytes)}</TableCell>
                                        <TableCell>
                                            <Typography variant="caption" sx={{ whiteSpace: "pre-line" }}>
                                                {v.remarks || "-"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                            <Tooltip title={l10n.getString("report-template-download", null, "Download")}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={busyVersion !== null}
                                                        onClick={() => { void handleDownload(v.version) }}
                                                    >
                                                        <DownloadIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            {!v.isCurrent && (
                                                <RestoreIconButton
                                                    tooltip={l10n.getString("report-template-restore", null, "Przywróć jako aktualną")}
                                                    disabled={busyVersion !== null}
                                                    onClick={() => { void handleRestore(v.version) }}
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button size="small" variant="outlined" onClick={onClose}>
                    {l10n.getString("close", null, "Zamknij")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
